import React, { useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import type { BikeWithLocation } from "../../types/bike";
import { cn } from "../../utils/helpers";
import { useVehicleCategoryStore } from "../../store/useVehicleCategoryStore";

interface SingleBikeSelectorProps {
  bikes: BikeWithLocation[];
  selectedBike: string | null;
  onChange: (selectedBike: string | null) => void;
  placeholder?: string;
}

export const SingleBikeSelector: React.FC<SingleBikeSelectorProps> = ({
  bikes,
  selectedBike,
  onChange,
  placeholder = "Select a bike",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { getCategoryById } = useVehicleCategoryStore();

  const filteredBikes = bikes.filter((bike) =>
    bike.bike_id.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const selectedBikeData = bikes.find((bike) => bike.bike_id === selectedBike);

  const handleBikeSelect = (bikeId: string) => {
    onChange(bikeId === selectedBike ? null : bikeId);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearchTerm("");
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-w-[200px]"
      >
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          {selectedBikeData ? (
            <div className="flex-1 text-left min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">
                {selectedBikeData.bike_id}
              </div>
            </div>
          ) : (
            <span className="text-sm text-gray-500">{placeholder}</span>
          )}
        </div>

        <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
          {selectedBike && (
            <div
              onClick={handleClear}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
              title="Clear selection"
            >
              <svg
                className="w-3 h-3 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          )}
          <ChevronDown
            className={cn(
              "w-4 h-4 transition-transform text-gray-400",
              isOpen && "rotate-180",
            )}
          />
        </div>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-1 w-full min-w-[300px] bg-white border border-gray-200 rounded-md shadow-lg z-[9999]">
            {/* Search */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search bikes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>
            </div>

            {/* Clear Selection */}
            {selectedBike && (
              <div className="p-2 border-b border-gray-200">
                <button
                  onClick={() => {
                    onChange(null);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                  className="flex items-center space-x-2 w-full p-2 hover:bg-gray-100 rounded text-sm transition-colors text-red-600"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  <span>Clear Selection</span>
                </button>
              </div>
            )}

            {/* Bike List */}
            <div className="max-h-64 overflow-y-auto">
              {filteredBikes.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  {searchTerm ? "No bikes found" : "No bikes available"}
                </div>
              ) : (
                filteredBikes.map((bike) => {
                  const isSelected = selectedBike === bike.bike_id;

                  return (
                    <button
                      key={bike.bike_id}
                      onClick={() => handleBikeSelect(bike.bike_id)}
                      className={cn(
                        "flex items-center space-x-3 w-full p-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 text-left",
                        isSelected && "bg-blue-50 hover:bg-blue-100",
                      )}
                    >
                      {/* Selection Indicator */}
                      <div
                        className={cn(
                          "w-2 h-2 rounded-full flex-shrink-0",
                          isSelected
                            ? "bg-blue-600"
                            : "bg-transparent border border-gray-300",
                        )}
                      />

                      {/* Bike Info */}
                      <div className="flex-1 min-w-0">
                        <div
                          className={cn(
                            "text-sm font-medium truncate",
                            isSelected ? "text-blue-900" : "text-gray-900",
                          )}
                        >
                          {bike.bike_id}
                          <span className="text-xs text-gray-500 truncate ml-2">
                            {getCategoryById(bike.category_id)}
                          </span>
                        </div>
                      </div>

                      {/* Status Indicator */}
                      <div
                        className={cn(
                          "w-2 h-2 rounded-full flex-shrink-0",
                          bike.status === "active"
                            ? "bg-green-500"
                            : bike.status === "maintenance"
                              ? "bg-red-500"
                              : "bg-gray-400",
                        )}
                      />
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
